import { GeographyRegion } from '@api/api/geographies';
import { AutocompleteInput } from '@design/components/dropdown/autocomplete-input/AutocompleteInput';
import { Dropdown, DropdownProps } from '@design/components/dropdown/Dropdown';
import { Icon } from '@design/components/icon/Icon';
import { useSearchGeographies } from '@terminal/react-query-hooks/geographies/useSearchGeographies';
import clsx from 'clsx';
import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FollowingSearchEmptyState } from '../../../following-search-empty-state/FollowingSearchEmptyState';
import { FollowCountryPill } from '../follow-country-pill/FollowCountryPill';
import { FollowCountryRow } from '../follow-country-row/FollowCountryRow';
import { FollowRegionRow } from '../follow-region-row/FollowRegionRow';
import {
  FollowedCountry,
  getRegionState,
  mapSelectedCountries,
} from '../utils';
import styles from './FollowGeographiesSearchBar.module.scss';
import {
  buildItemKey,
  DropdownItem,
  getExpandedRegionCountryItems,
} from './utils';

export type FollowGeographiesSearchBarVariant = 'onboarding' | 'drawer';

export interface FollowGeographiesSearchBarProps {
  regions: GeographyRegion[];
  selectedCountries: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: FollowGeographiesSearchBarVariant;
  className?: string;
}

/**
 * @deprecated
 */
export const FollowGeographiesSearchBar = ({
  regions,
  selectedCountries,
  onSelectionChange,
  isOpen,
  onOpenChange,
  variant = 'onboarding',
  className,
}: FollowGeographiesSearchBarProps) => {
  const { t } = useTranslation('onboarding');
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(
    new Set()
  );

  const {
    value: searchValue,
    debouncedSearchTerm,
    data: searchResults,
    isSearching,
    handleChange,
    onClear,
  } = useSearchGeographies();

  const isSearchActive = !!searchValue.trim() && !!debouncedSearchTerm;

  const handleToggleCountry = (code: string) => {
    const next = new Set(selectedCountries);

    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }

    onSelectionChange(next);
  };

  const handleToggleRegion = (region: GeographyRegion) => {
    const state = getRegionState(region, selectedCountries);
    const next = new Set(selectedCountries);

    if (state === 'all') {
      region.countries.forEach(country => next.delete(country.code));
    } else {
      region.countries.forEach(country => next.add(country.code));
    }

    onSelectionChange(next);
  };

  const handleToggleExpansion = (regionCode: string) =>
    setExpandedRegions(current => {
      const next = new Set(current);

      if (next.has(regionCode)) {
        next.delete(regionCode);
      } else {
        next.add(regionCode);
      }

      return next;
    });

  const dropdownItems = useMemo<DropdownItem[]>(
    () =>
      isSearchActive
        ? searchResults.flatMap<DropdownItem>(result => {
            if (result.type === 'region') {
              const fullRegion = regions.find(
                region => region.code === result.code
              );
              const region = fullRegion ?? {
                code: result.code,
                name: result.name,
                countries: [],
              };

              const items: DropdownItem[] = [
                { kind: 'region', region, expandable: true },
                ...getExpandedRegionCountryItems(region, expandedRegions),
              ];

              return items;
            }

            return [
              {
                kind: 'country',
                country: { code: result.code, name: result.name },
                indented: false,
              },
            ];
          })
        : regions.flatMap<DropdownItem>(region => {
            const items: DropdownItem[] = [
              { kind: 'region', region, expandable: true },
              ...getExpandedRegionCountryItems(region, expandedRegions),
            ];

            return items;
          }),
    [isSearchActive, searchResults, regions, expandedRegions]
  );

  const renderItem: DropdownProps<DropdownItem>['renderDropdownItem'] =
    item => {
      if (item.kind === 'region') {
        const state = getRegionState(item.region, selectedCountries);
        return (
          <FollowRegionRow
            key={buildItemKey(item)}
            regionCode={item.region.code}
            regionName={item.region.name}
            countryCount={item.region.countries.length}
            state={state}
            expandable={item.expandable}
            isExpanded={expandedRegions.has(item.region.code)}
            onToggle={() => handleToggleRegion(item.region)}
            onExpandToggle={() => handleToggleExpansion(item.region.code)}
            highlighted={isSearchActive ? debouncedSearchTerm : undefined}
          />
        );
      }

      return (
        <FollowCountryRow
          key={buildItemKey(item)}
          countryCode={item.country.code}
          countryName={item.country.name}
          isSelected={selectedCountries.has(item.country.code)}
          onToggle={() => handleToggleCountry(item.country.code)}
          indented={item.indented}
          highlighted={isSearchActive ? debouncedSearchTerm : undefined}
        />
      );
    };

  const handleOpen = () => onOpenChange(true);

  const handleClose = () => {
    onClear();
    onOpenChange(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onOpenChange(true);
    handleChange(e);
  };

  const showEmptyState =
    isSearchActive && !isSearching && searchResults.length === 0;
  const pills = useMemo(
    () => mapSelectedCountries(regions, selectedCountries),
    [regions, selectedCountries]
  );
  const showPills = pills.length > 0 && (variant === 'drawer' || !isOpen);
  const hasContentBelowInput = isOpen || showPills;

  const handleRemovePill = (followedCountry: FollowedCountry) =>
    handleToggleCountry(followedCountry.code);

  const clearButton = searchValue ? (
    <button
      type="button"
      className={styles.clearButton}
      onClick={onClear}
      data-testid="follow-geographies-clear"
    >
      {t('onboarding:followGeographies.clear')}
    </button>
  ) : null;

  return (
    <div
      className={clsx(
        styles.wrapper,
        isOpen && styles.wrapperActive,
        hasContentBelowInput && styles.wrapperHasContentBelow,
        className
      )}
      data-testid="geographies-search-bar"
    >
      <Dropdown<DropdownItem>
        className={styles.dropdown}
        dropdownItems={dropdownItems}
        renderDropdownItem={renderItem}
        shouldCloseOnSelect={false}
        shouldSetActiveOnSelect={false}
        shouldShowItems={isOpen}
        onFocus={variant === 'drawer' ? undefined : handleOpen}
        onClick={handleOpen}
        onOutsideClick={handleClose}
        strategy="fixed"
        offsetValue={{ x: 0, y: 0 }}
        afterInListNode={
          showEmptyState ? <FollowingSearchEmptyState /> : undefined
        }
      >
        <AutocompleteInput
          className={styles.searchInput}
          value={searchValue}
          onChange={handleInputChange}
          leftIcon={<Icon iconName="Search" size={20} />}
          rightIcon={clearButton}
          placeholder={t('onboarding:followGeographies.searchPlaceholder')}
          autoFocus={variant === 'drawer'}
        />
      </Dropdown>
      {showPills && (
        <div
          className={styles.pillsWrapper}
          data-testid="follow-geographies-pills"
        >
          {pills.map(followedCountry => (
            <FollowCountryPill
              key={`${followedCountry.code}`}
              followedCountry={followedCountry}
              onRemove={() => handleRemovePill(followedCountry)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
